import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Fire, Trophy, X, ShareNetwork, Copy, Check } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function StreakBadge() {
  const [streak, setStreak] = useState(null);
  const [milestoneModal, setMilestoneModal] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchAndCheckin = async () => {
      try {
        const { data } = await axios.post(`${API_URL}/api/streak/checkin`);
        setStreak(data);
        if (data.new_milestone) setMilestoneModal(data.new_milestone);
      } catch {
        try {
          const { data } = await axios.get(`${API_URL}/api/streak`);
          setStreak(data);
        } catch {}
      }
    };
    fetchAndCheckin();
  }, []);

  const shareStreak = async () => {
    const text = `${streak.current_streak} days of consistent marketing with ScalitOS! My longest streak: ${streak.longest_streak} days.`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'My ScalitOS Streak', text });
        return;
      } catch {}
    }
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!streak || streak.current_streak === 0) return null;

  return (
    <>
      <div data-testid="streak-badge" className="flex items-center gap-2 px-3 py-2 rounded-xl cursor-default" style={{ background: 'rgba(200,240,96,0.06)', border: '1px solid rgba(200,240,96,0.1)' }}>
        <Fire size={18} weight="fill" style={{ color: streak.current_streak >= 7 ? '#f59e0b' : '#c8f060' }} />
        <span className="text-sm font-medium" style={{ color: '#f5f0e8', fontFamily: "'DM Sans'" }}>{streak.current_streak}</span>
        <span className="text-xs" style={{ color: 'rgba(245,240,232,0.35)' }}>day streak</span>
      </div>

      {/* Milestone Celebration Modal */}
      <AnimatePresence>
        {milestoneModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)' }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className="w-full max-w-sm mx-4 rounded-2xl overflow-hidden relative"
              style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.07)' }}
              data-testid="streak-milestone-modal"
            >
              <button onClick={() => setMilestoneModal(null)} className="absolute top-4 right-4 text-zinc-500 hover:text-white">
                <X size={18} />
              </button>
              <div className="p-8 text-center">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}>
                  <Fire size={56} weight="fill" className="mx-auto mb-4" style={{ color: '#f59e0b' }} />
                </motion.div>
                <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                  className="text-5xl font-bold mb-2" style={{ fontFamily: "'Instrument Serif', serif", color: '#c8f060' }}>
                  {milestoneModal.level}
                </motion.p>
                <p className="text-lg font-medium mb-1" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8' }}>
                  {milestoneModal.message}
                </p>
                <p className="text-xs mb-6" style={{ color: 'rgba(245,240,232,0.35)' }}>{milestoneModal.label}</p>

                {/* Share Card Preview */}
                <div className="rounded-xl p-5 mb-4 text-left" style={{ background: '#080808', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Fire size={20} weight="fill" style={{ color: '#f59e0b' }} />
                    <span className="text-xs uppercase tracking-wider" style={{ color: '#c8f060' }}>Streak Milestone</span>
                  </div>
                  <p className="text-3xl font-bold mb-1" style={{ fontFamily: "'Instrument Serif', serif", color: '#c8f060' }}>{milestoneModal.level} days</p>
                  <p className="text-sm mb-3" style={{ color: 'rgba(245,240,232,0.6)' }}>of consistent marketing</p>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px]" style={{ color: 'rgba(245,240,232,0.2)' }}>Made with ScalitOS</span>
                    <Trophy size={14} style={{ color: 'rgba(245,240,232,0.15)' }} />
                  </div>
                </div>

                <div className="flex gap-2">
                  <button onClick={() => setMilestoneModal(null)}
                    className="flex-1 py-2.5 rounded-full text-sm transition-all"
                    style={{ border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(245,240,232,0.5)' }}>
                    Close
                  </button>
                  <button onClick={shareStreak} data-testid="share-milestone-btn"
                    className="flex-1 py-2.5 rounded-full text-sm font-medium flex items-center justify-center gap-2 transition-all"
                    style={{ background: '#c8f060', color: '#080808' }}>
                    {copied ? <><Check size={14} />Copied!</> : <><ShareNetwork size={14} />Share</>}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export function StreakCard() {
  const [streak, setStreak] = useState(null);
  const [review, setReview] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [streakRes, reviewRes] = await Promise.all([
          axios.get(`${API_URL}/api/streak`),
          axios.get(`${API_URL}/api/streak/monthly-review`),
        ]);
        setStreak(streakRes.data);
        setReview(reviewRes.data);
      } catch {}
    };
    fetchAll();
  }, []);

  if (!streak) return null;

  const progress = streak.milestone?.next
    ? Math.min(100, (streak.current_streak / streak.milestone.next) * 100)
    : 100;

  // Build monthly calendar grid
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
  const activeDaysSet = new Set((review?.active_days || streak.monthly_active_days || []).map(d => parseInt(d.split('-')[2])));
  const todayDate = now.getUTCDate();
  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return (
    <div data-testid="streak-card" className="p-5" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '14px' }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Fire size={20} weight="fill" style={{ color: streak.current_streak >= 7 ? '#f59e0b' : '#c8f060' }} />
          <h3 className="font-medium" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8' }}>Monthly Activity</h3>
        </div>
        <div className="flex items-center gap-2">
          {streak.milestone?.label && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ background: 'rgba(245,158,11,0.1)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.2)' }}>
              {streak.milestone.label}
            </span>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="flex items-end gap-4 mb-4">
        <div>
          <p className="text-3xl font-bold" style={{ fontFamily: "'Instrument Serif', serif", color: '#c8f060' }}>{streak.current_streak}</p>
          <p className="text-xs" style={{ color: 'rgba(245,240,232,0.35)' }}>day streak</p>
        </div>
        <div>
          <p className="text-lg font-medium" style={{ fontFamily: "'Instrument Serif', serif", color: 'rgba(245,240,232,0.5)' }}>{streak.longest_streak}</p>
          <p className="text-xs" style={{ color: 'rgba(245,240,232,0.25)' }}>longest</p>
        </div>
        {review && (
          <div className="ml-auto text-right">
            <p className="text-lg font-medium" style={{ fontFamily: "'Instrument Serif', serif", color: 'rgba(200,240,96,0.6)' }}>{review.completion_pct}%</p>
            <p className="text-xs" style={{ color: 'rgba(245,240,232,0.25)' }}>{monthNames[month]} activity</p>
          </div>
        )}
      </div>

      {/* Monthly Calendar Grid */}
      <div className="mb-3">
        <div className="grid grid-cols-7 gap-1 mb-1">
          {['S','M','T','W','T','F','S'].map((d, i) => (
            <div key={i} className="text-center text-[8px] py-0.5" style={{ color: 'rgba(245,240,232,0.2)' }}>{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} className="w-full aspect-square" />)}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const isActive = activeDaysSet.has(day);
            const isToday = day === todayDate;
            return (
              <div key={day} className="w-full aspect-square rounded-sm flex items-center justify-center text-[8px] font-medium transition-all"
                style={{
                  background: isActive ? 'rgba(200,240,96,0.15)' : isToday ? 'rgba(255,255,255,0.04)' : 'transparent',
                  color: isActive ? '#c8f060' : isToday ? 'rgba(245,240,232,0.5)' : 'rgba(245,240,232,0.12)',
                  border: isToday ? '1px solid rgba(200,240,96,0.2)' : '1px solid transparent',
                }}>
                {day}
              </div>
            );
          })}
        </div>
      </div>

      {/* Active days count */}
      <div className="flex items-center justify-between text-[10px] mb-3" style={{ color: 'rgba(245,240,232,0.3)' }}>
        <span>{activeDaysSet.size} active days in {monthNames[month]}</span>
        <span>{daysInMonth - activeDaysSet.size} days remaining</span>
      </div>

      {/* Progress to next milestone */}
      {streak.milestone?.next && (
        <div>
          <div className="flex justify-between text-[10px] mb-1" style={{ color: 'rgba(245,240,232,0.3)' }}>
            <span>Next: {streak.milestone.next} day streak</span>
            <span>{streak.current_streak}/{streak.milestone.next}</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <motion.div initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ duration: 1 }} className="h-full rounded-full" style={{ background: '#c8f060' }} />
          </div>
        </div>
      )}
    </div>
  );
}
